import {
  sameReadinessBinding,
  UI_READINESS_POLICY,
  type UiReadinessBinding,
  type UiReadinessControls,
  type UiReadinessDependency,
  type UiReadinessFact,
  type UiReadinessFailure,
  type UiReadinessObservation,
  type UiReadinessOpen,
  type UiReadinessState,
  type UiReadinessTime,
  type UiReadinessVisibility,
} from './ui-readiness-contract'

interface UiReadinessDependencies {
  now: () => UiReadinessTime
  nextSequence?: () => number
  visibility: () => UiReadinessVisibility
  emit: (fact: UiReadinessFact) => void
  reportError: (error: Error) => void
}

interface View {
  binding?: UiReadinessBinding
  readonly started: UiReadinessTime
  readonly waits: Map<UiReadinessDependency, UiReadinessTime>
  initialized: boolean
  state?: UiReadinessState & {eventId: string}
  controls?: {value: UiReadinessControls; stateEventId: string}
  failed: boolean
  ready: boolean
  disposed: boolean
}

function validTime(time: UiReadinessTime): boolean {
  return (
    time.clockId.length > 0 &&
    Number.isFinite(time.observedAtMs) &&
    time.observedAtMs >= 0 &&
    Number.isFinite(time.monotonicMs) &&
    time.monotonicMs >= 0
  )
}

function elapsed(start: UiReadinessTime, end: UiReadinessTime): number | undefined {
  if (start.clockId !== end.clockId || end.monotonicMs < start.monotonicMs) return undefined
  return end.monotonicMs - start.monotonicMs
}

function controlsUsable(controls: UiReadinessControls): boolean {
  if (!controls.connected) return false
  return controls.role === 'writable'
    ? controls.editable &&
        controls.editHandlerAttached &&
        controls.actionHandlerAttached &&
        controls.validInputActionEnabled
    : !controls.editable && controls.mutationActionsDisabled
}

export function createBrowserUiReadinessTracker(dependencies: UiReadinessDependencies, open: UiReadinessOpen) {
  const views = new Map<string, View>()
  let sequence = 0
  let openObserved = false
  let closed = false
  let openedAt = open.origin
  let originRequestId = open.originRequestId

  const report = (message: string) => dependencies.reportError(new Error(message))
  const emit = (
    observation: UiReadinessObservation,
    time: UiReadinessTime,
    viewId?: string,
    evidenceSource: UiReadinessFact['evidence_source'] = 'destination',
  ): string | undefined => {
    const eventSequence = dependencies.nextSequence ? dependencies.nextSequence() : sequence++
    if (!validTime(time) || !Number.isSafeInteger(eventSequence) || eventSequence < 0) {
      report('Invalid UI readiness clock or sequence')
      return
    }
    const view = viewId ? views.get(viewId) : undefined
    const binding = view?.state?.binding ?? view?.binding
    const eventId = `${open.clientOpenId}/${time.clockId}/${eventSequence}`
    const fact: UiReadinessFact = {
      ...UI_READINESS_POLICY,
      event_id: eventId,
      client_open_id: open.clientOpenId,
      ...(viewId ? {client_view_id: viewId} : {}),
      clock_id: time.clockId,
      sequence: eventSequence,
      observed_at_ms: time.observedAtMs,
      monotonic_ms: time.monotonicMs,
      ...(originRequestId
        ? {origin_request_id: originRequestId, origin_request_id_source: 'server_payload' as const}
        : {}),
      ...(openedAt
        ? {
            origin_clock_id: openedAt.clockId,
            origin_observed_at_ms: openedAt.observedAtMs,
            origin_monotonic_ms: openedAt.monotonicMs,
          }
        : {}),
      surface: open.surface,
      entry_kind: open.entryKind,
      repository_scope: open.repositoryScope,
      role: view?.state?.role ?? 'unknown',
      visibility: dependencies.visibility(),
      evidence_source: evidenceSource,
      ...(binding ? {binding_kind: binding.kind} : {}),
      ...(binding?.kind === 'session'
        ? {
            session_id: binding.sessionId,
            ...(binding.environmentId ? {environment_id: binding.environmentId} : {}),
            ...(binding.taskId ? {task_id: binding.taskId} : {}),
            ...(binding.chatId ? {chat_id: binding.chatId} : {}),
          }
        : {}),
      ...observation,
    }
    try {
      dependencies.emit(fact)
    } catch {
      // Report a bounded diagnostic, never the thrown value or user-containing context.
      report('Unable to emit UI readiness observation')
    }
    return eventId
  }

  const activeView = (viewId: string): View | undefined => {
    const view = views.get(viewId)
    return !closed && view && !view.disposed && !view.failed ? view : undefined
  }

  const fail = (viewId: string, reason: UiReadinessFailure, time = dependencies.now()) => {
    const view = activeView(viewId)
    if (!view || view.ready) return
    view.failed = true
    emit({observation: 'failure', reason}, time, viewId)
  }

  const observeControls = (viewId: string) => {
    const view = activeView(viewId)
    if (!view || view.ready || !view.initialized || !view.state || !view.controls || view.waits.size > 0) return
    if (view.controls.stateEventId !== view.state.eventId) return
    const controls = view.controls.value
    if (!sameReadinessBinding(view.state.binding, controls.binding)) {
      fail(viewId, 'session_mismatch')
      return
    }
    if (view.state.role !== controls.role) {
      fail(viewId, 'role_mismatch')
      return
    }
    if (!controlsUsable(controls) || dependencies.visibility() !== 'visible') return
    const time = dependencies.now()
    const duration = elapsed(view.state.time, time)
    if (duration === undefined) {
      report('UI readiness state and controls do not share an ordered local clock')
      return
    }
    const eventId = emit(
      {
        observation: 'input_ready',
        state_event_id: view.state.eventId,
        state_to_input_elapsed_ms: duration,
        binding_kind: view.state.binding.kind,
      },
      time,
      viewId,
    )
    if (eventId) {
      view.ready = true
      closed = true
    }
  }

  return {
    setOriginRequestId(requestId: string) {
      originRequestId ??= requestId
    },
    isReady(viewId: string) {
      return views.get(viewId)?.ready === true
    },
    opened() {
      if (openObserved) return
      openObserved = true
      openedAt ??= dependencies.now()
      emit({observation: 'open'}, openedAt, undefined, 'origin_shell')
    },
    startView(viewId: string, binding?: UiReadinessBinding, started: UiReadinessTime = dependencies.now()) {
      if (closed) {
        report('A completed UI readiness open requires a new open identity')
        return
      }
      if (views.has(viewId)) return
      if (!validTime(started)) {
        report('Invalid UI readiness initialization clock')
        return
      }
      views.set(viewId, {
        binding,
        started,
        waits: new Map(),
        initialized: false,
        failed: false,
        ready: false,
        disposed: false,
      })
      emit({observation: 'initialization_started'}, started, viewId)
    },
    bindView(viewId: string, binding: UiReadinessBinding) {
      const view = activeView(viewId)
      if (!view || view.ready) return
      if (open.expectedTaskId) {
        if (binding.kind !== 'session' || !binding.taskId) return
        if (binding.taskId !== open.expectedTaskId) {
          fail(viewId, 'session_mismatch')
          return
        }
      }
      if (view.binding && !sameReadinessBinding(view.binding, binding)) {
        fail(viewId, 'session_mismatch')
        return
      }
      view.binding = binding
    },
    initializationCompleted(viewId: string, time = dependencies.now()) {
      const view = activeView(viewId)
      if (!view || view.initialized) return
      const localDuration = elapsed(view.started, time)
      if (localDuration === undefined) {
        report('UI readiness initialization does not share an ordered local clock')
        return
      }
      view.initialized = true
      const originDuration = openedAt ? elapsed(openedAt, time) : undefined
      emit(
        {
          observation: 'initialization_completed',
          initialization_local_elapsed_ms: localDuration,
          ...(originDuration === undefined
            ? {initialization_timing: 'clock_unresolved'}
            : {initialization_timing: 'origin_clock', initialization_elapsed_ms: originDuration}),
        },
        time,
        viewId,
      )
      observeControls(viewId)
    },
    stateAvailable(viewId: string, state: UiReadinessState) {
      const view = activeView(viewId)
      if (!view || view.ready) return
      if (!view.binding) return
      if (!sameReadinessBinding(view.binding, state.binding)) {
        fail(viewId, 'session_mismatch')
        return
      }
      if (!validTime(state.time) || elapsed(view.started, state.time) === undefined) {
        report('Invalid UI readiness state observation clock')
        return
      }
      if (view.state?.role === state.role && sameReadinessBinding(view.state.binding, state.binding)) {
        return view.state.eventId
      }
      view.controls = undefined
      // Assign before emission so the fact carries the witnessed role, not the previous one.
      view.state = {...state, eventId: ''}
      const eventId = emit(
        {observation: 'required_state_available', state_source: state.source, binding_kind: state.binding.kind},
        state.time,
        viewId,
        state.source === 'snapshot' || state.source === 'action' ? 'relay' : 'destination',
      )
      if (!eventId) {
        view.state = undefined
        return
      }
      view.state.eventId = eventId
      return eventId
    },
    controlsObserved(viewId: string, controls: UiReadinessControls, stateEventId: string) {
      const view = activeView(viewId)
      if (!view || view.ready) return
      view.controls = {value: controls, stateEventId}
      observeControls(viewId)
    },
    controlsUnavailable(viewId: string) {
      const view = activeView(viewId)
      if (view) view.controls = undefined
    },
    dependencyChanged(
      viewId: string,
      dependency: UiReadinessDependency,
      waiting: boolean,
      time: UiReadinessTime = dependencies.now(),
    ) {
      const view = activeView(viewId)
      if (!view || view.ready || view.waits.has(dependency) === waiting) return
      if (waiting) {
        view.waits.set(dependency, time)
        view.state = undefined
        view.controls = undefined
        emit({observation: 'dependency_wait_started', dependency}, time, viewId)
      } else {
        const start = view.waits.get(dependency)
        const duration = start ? elapsed(start, time) : undefined
        view.waits.delete(dependency)
        view.controls = undefined
        if (duration === undefined) {
          report('UI readiness dependency wait does not share an ordered local clock')
          return
        }
        emit({observation: 'dependency_wait_ended', dependency, dependency_wait_elapsed_ms: duration}, time, viewId)
      }
    },
    failure: fail,
    visibilityChanged() {
      for (const viewId of views.keys()) observeControls(viewId)
    },
    disposeView(viewId: string, reason: 'closed' | 'replaced' = 'closed') {
      const view = views.get(viewId)
      if (!view || view.disposed) return
      view.disposed = true
      view.controls = undefined
      emit({observation: 'view_disposed', reason}, dependencies.now(), viewId)
    },
    pageExit() {
      for (const [viewId, view] of views) {
        if (!view.disposed) emit({observation: 'page_exit', delivery: 'best_effort'}, dependencies.now(), viewId)
      }
    },
  }
}

export type BrowserUiReadinessTracker = ReturnType<typeof createBrowserUiReadinessTracker>
